import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {useUserConfigContext} from "./UserConfigContext";

export const RoomConfigContext = createContext({});

export const FloorColors = {
    Light: "#eeeef0",
    Dark: "#101018",
    Red: "#800000",
    Blue: "#243956",
};

export const AisleMode = {
    // these values are made to be a bit mask!
    Left: 1,
    Right: 2,
    Both: 3
}

export const SpeakerType = {
    Normal: 0,  // can be built in the walls or used separately
    Waterfall: 1  // MUST be built in (waterfall), and speakers are at a defined position
}

export default function RoomConfigProvider({children}) {

    let defaultWidth = 400;
    let defaultHeight = 250;
    let defaultDepth = 500;
    let defaultFloorColor = FloorColors.Dark;
    let defaultWallColor = "#101020";
    let defaultCeilingColor = "#202028";
    let defaultAisleWidth = 50;
    let defaultAisleMode = AisleMode.Both;
    let defaultFloorLedsOn = true;
    let defaultTransparentWalls = false;
    let defaultRoofHeight = 0;

    const { userConfig } = useUserConfigContext();

    if (userConfig) {
        const config = userConfig;
        defaultWidth = config.width;
        defaultHeight = config.height;
        defaultDepth = config.depth;
        defaultFloorColor = config.floorColor;
        defaultAisleWidth = config.aisleWidth;
        defaultAisleMode = config.aisleMode;
        defaultFloorLedsOn = config.floorLedsOn;
        defaultTransparentWalls = config.transparentWalls;
        defaultRoofHeight = config.roofHeight;
    }

    const [width, setWidth] = useState(defaultWidth);
    const [height, setHeight] = useState(defaultHeight);
    const [depth, setDepth] = useState(defaultDepth);
    const [roofHeight, setRoofHeight] = useState(defaultRoofHeight);
    const [floorColor, setFloorColor] = useState(defaultFloorColor);
    const [wallColor, setWallColor] = useState(defaultWallColor);
    const [ceilingColor, setCeilingColor] = useState(defaultCeilingColor);
    const [aisleWidth, setAisleWidth] = useState(defaultAisleWidth);
    const [aisleMode, _setAisleMode] = useState(defaultAisleMode);
    const [mainLightOn, setMainLightOn] = useState(true);
    const [transparentWalls, setTransparentWalls] = useState(defaultTransparentWalls);
    const [floorLedsOn, setFloorLedsOn] = useState(defaultFloorLedsOn);
    const [frontMargin, setFrontMargin] = useState(0);
    const [ceilingMargin, setCeilingMargin] = useState(0);
    const [sideMargin, setSideMargin] = useState(0);
    const [backMargin, setBackMargin] = useState(0);
    const setAisleMode = useCallback(v => _setAisleMode(parseInt(v)), []);

    // these take into account the margins
    const freeWidth = useMemo(() => width - sideMargin * 2, [width, sideMargin]);
    const freeHeight = useMemo(() => height - ceilingMargin, [height, ceilingMargin]);
    const freeDepth = useMemo(() => depth - frontMargin - backMargin, [depth, backMargin, frontMargin]);

    useEffect(() => {
       setHeight(parseInt(height));
       setRoofHeight(parseInt(roofHeight));
    }, [height, roofHeight, setHeight, setRoofHeight]);

    return (
        <RoomConfigContext.Provider value={{
            width, setWidth,
            height, setHeight,
            depth, setDepth,
            roofHeight, setRoofHeight,
            aisleWidth, setAisleWidth,
            floorColor, setFloorColor,
            wallColor, setWallColor,
            ceilingColor, setCeilingColor,
            mainLightOn, setMainLightOn,
            floorLedsOn, setFloorLedsOn,
            aisleMode, setAisleMode,
            // the available space counting margins
            freeWidth, freeHeight, freeDepth,
            transparentWalls, setTransparentWalls,
            // to be set by audio context, NOT by menu
            frontMargin, setFrontMargin,
            ceilingMargin, setCeilingMargin,
            sideMargin, setSideMargin,
            backMargin, setBackMargin

        }}>
            {children}
        </RoomConfigContext.Provider>
    );
}

export const useRoomConfig = () => useContext(RoomConfigContext);
